import { Component, Inject, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { SelectButton } from "app/common/select-buttons/select-buttons.component";
import { DeviceRecognizerHelper } from "app/core/helpers/device-recognizer.helper";
import { UserTwoFactorHttpService } from "app/modules/user/services/http/user-two-factor.http.service";
import { CookieService } from "ngx-cookie-service";
import { SKIP_SET_UP_2FA } from "app/common";

export enum OTP_TYPE {
  EMAIL = "email",
  DEVICE = "device"
}

enum TWO_FACTOR_MODAL_STEPS {
  SELECT_SECTION = "select_section",
  QR_CODE_SECTION = "qr_code_section",
  PASTE_CODE_SECTION = "paste_code_section",
  STATIC_TOKENS = "static_tokens"
}

type DeviceOtp = {
  otp_secret?: string;
  qr_code?: string;
  message?: string;
};

type ModalDataType = {
  force_otp: boolean;
  is_change_mode: boolean;
  chosen_otp?: OTP_TYPE;
};

@Component({
  selector: "app-2fa-set-up-dialog",
  templateUrl: "./2fa-set-up-dialog.component.html",
  styleUrls: ["./2fa-set-up-dialog.component.scss"],
  standalone: false
})
export class TwoFactorSetUpDialogComponent implements OnInit {
  public chosenOtpType: OTP_TYPE;
  public currentStep: TWO_FACTOR_MODAL_STEPS = TWO_FACTOR_MODAL_STEPS.SELECT_SECTION;
  public modalSteps = TWO_FACTOR_MODAL_STEPS;
  public otpTypes = OTP_TYPE;
  public authCode: string;
  public isCodeCopied = false;
  public isEmailCodeSend = false;
  public deviceOtpData: DeviceOtp = { otp_secret: "", qr_code: "", message: "" };
  public isVerifying = false;
  public errors: string[];
  public tokensDownloadUrl: string;
  public staticTokens: Array<string> = ["6art45kp", "7qq7lxcb", "nnfkawip", "tsylvmj5", "5mkulmif"];
  public isChangeMode = false;
  constructor(
    private twoFactorHttpService: UserTwoFactorHttpService,
    @Inject(MAT_DIALOG_DATA) public data: ModalDataType,
    public dialogRef: MatDialogRef<TwoFactorSetUpDialogComponent>,
    private translate: TranslateService,
    private cookieService: CookieService
  ) {
    this.isChangeMode = data.is_change_mode;

    if (data.chosen_otp) this.chosenOtpType = data.chosen_otp;
  }

  ngOnInit(): void {
    this.dialogRef.disableClose = true;
  }

  public onSelectButtonClick(type: OTP_TYPE): void {
    this.chosenOtpType = type;
  }

  public onSelectButtonDelete(): void {
    this.chosenOtpType = null;
  }

  public goToNextStep(): void {
    if (this.currentStep === TWO_FACTOR_MODAL_STEPS.SELECT_SECTION && this.chosenOtpType === OTP_TYPE.DEVICE) {
      this.currentStep = TWO_FACTOR_MODAL_STEPS.QR_CODE_SECTION;
      this.handleSetUpTwoFactorAuthentication();
      return;
    }

    if (this.currentStep === TWO_FACTOR_MODAL_STEPS.SELECT_SECTION && this.chosenOtpType === OTP_TYPE.EMAIL) {
      this.currentStep = TWO_FACTOR_MODAL_STEPS.PASTE_CODE_SECTION;
      this.handleSetUpTwoFactorAuthentication();
      return;
    }

    if (this.currentStep === TWO_FACTOR_MODAL_STEPS.QR_CODE_SECTION) {
      this.currentStep = TWO_FACTOR_MODAL_STEPS.PASTE_CODE_SECTION;
      this.isCodeCopied = false;
      return;
    }

    if (this.currentStep === TWO_FACTOR_MODAL_STEPS.PASTE_CODE_SECTION && this.chosenOtpType === OTP_TYPE.DEVICE) {
      this.currentStep = TWO_FACTOR_MODAL_STEPS.STATIC_TOKENS;
    }

    throw new Error("[TwoFactorDialogComponent] no step detected in goToNextStep");
  }

  public goToPreviousStep(): void {
    if (this.currentStep === TWO_FACTOR_MODAL_STEPS.QR_CODE_SECTION) {
      this.currentStep = TWO_FACTOR_MODAL_STEPS.SELECT_SECTION;
      return;
    }

    if (this.currentStep === TWO_FACTOR_MODAL_STEPS.PASTE_CODE_SECTION && this.chosenOtpType === OTP_TYPE.EMAIL) {
      if (this.isEmailCodeSend) this.isEmailCodeSend = false;
      this.currentStep = TWO_FACTOR_MODAL_STEPS.SELECT_SECTION;
      return;
    }

    if (this.currentStep === TWO_FACTOR_MODAL_STEPS.PASTE_CODE_SECTION && this.chosenOtpType === OTP_TYPE.DEVICE) {
      this.currentStep = TWO_FACTOR_MODAL_STEPS.QR_CODE_SECTION;
      this.handleSetUpTwoFactorAuthentication();
      return;
    }

    throw new Error("[TwoFactorDialogComponent] no step detected in goToPreviousStep");
  }

  public handleModalTitle(): string {
    switch (this.currentStep) {
      case TWO_FACTOR_MODAL_STEPS.SELECT_SECTION:
        return this.isChangeMode
          ? "MENU.ACCOUNT.TWO_FACTOR.TWO_FACTOR_CHANGE"
          : "TWO_FACTOR_MODAL.SELECT_OTP_VIEW.TITLE";
      case TWO_FACTOR_MODAL_STEPS.QR_CODE_SECTION:
        return "TWO_FACTOR_MODAL.QR_VIEW.TITLE";
      case TWO_FACTOR_MODAL_STEPS.PASTE_CODE_SECTION:
        return "TWO_FACTOR_MODAL.AUTH_VIEW.TITLE";
      case TWO_FACTOR_MODAL_STEPS.STATIC_TOKENS:
        return "TWO_FACTOR_MODAL.STATIC_TOKENS.TITLE";
      default:
        throw new Error("[TwoFactorDialogComponent] no step detected in handleModalTitle");
    }
  }

  public handleModalDescription(): string {
    switch (this.currentStep) {
      case TWO_FACTOR_MODAL_STEPS.SELECT_SECTION:
        return "TWO_FACTOR_MODAL.SELECT_OTP_VIEW.DESCRIPTION";
      case TWO_FACTOR_MODAL_STEPS.QR_CODE_SECTION:
        return "TWO_FACTOR_MODAL.QR_VIEW.DESCRIPTION";
      case TWO_FACTOR_MODAL_STEPS.PASTE_CODE_SECTION:
        return "TWO_FACTOR_MODAL.AUTH_VIEW.DESCRIPTION";
      case TWO_FACTOR_MODAL_STEPS.STATIC_TOKENS:
        return "TWO_FACTOR_MODAL.STATIC_TOKENS.DESCRIPTION";
      default:
        throw new Error("[TwoFactorDialogComponent] no step detected in handleModalDescription");
    }
  }

  public handleSetUpTwoFactorAuthentication(): void {
    const request = this.isChangeMode
      ? this.twoFactorHttpService.changeTwoFactorAuthentication(this.chosenOtpType)
      : this.twoFactorHttpService.setUpTwoFactorAuthentication(this.chosenOtpType);

    request.subscribe({
      next: (response: any) => {
        this.deviceOtpData = response;
      },
      error: (error) => {
        console.error(error);
        if (error?.error?.message) {
          this.errors = [error.error.message];
        } else if (error?.error?.verify_token) {
          this.errors = [error.error.verify_token];
        } else if (error?.error?.otp) {
          this.errors = [error.error.otp];
        } else {
          this.errors = [this.translate.instant("TWO_FACTOR_MODAL.ERROR")];
        }
      }
    });
  }

  public handleVerifySetUpTwoFactorAuthentication(): void {
    this.isVerifying = true;
    this.twoFactorHttpService.verifySetUpTwoFactorAuthentication(this.authCode).subscribe({
      next: (response: any) => {
        this.isVerifying = false;
        this.staticTokens = response.static_tokens;
        this.tokensDownloadUrl = response.download_url;
        this.currentStep = TWO_FACTOR_MODAL_STEPS.STATIC_TOKENS;
      },
      error: (error) => {
        this.isVerifying = false;
        console.error(error.error);
        if (error?.error?.message) {
          this.errors = [error.error.message];
        } else if (error?.error?.verify_token) {
          this.errors = [error.error.verify_token];
        } else if (error?.error?.otp) {
          this.errors = [error.error.otp];
        } else {
          this.errors = [this.translate.instant("TWO_FACTOR_MODAL.ERROR")];
        }
      }
    });
  }

  public handleSkipButton(): void {
    this.cookieService.set(SKIP_SET_UP_2FA, "true", {
      path: "/",
      secure: true,
      sameSite: "Strict"
    });

    this.twoFactorHttpService.disableUnconfirmedTwoFactorAuthentication().subscribe({
      next: () => {
        this.dialogRef.close();
      },
      error: (error) => {
        console.error(error);
        if (error?.error?.message) {
          this.errors = [error.error.message];
        } else if (error?.error?.verify_token) {
          this.errors = [error.error.verify_token];
        } else if (error?.error?.otp) {
          this.errors = [error.error.otp];
        } else {
          this.errors = [this.translate.instant("TWO_FACTOR_MODAL.ERROR")];
        }

        this.dialogRef.close();
      }
    });
  }

  public async copyToClipboard(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.deviceOtpData.otp_secret);
      this.isCodeCopied = true;
    } catch (err) {
      console.error("[TwoFactorDialogComponent]", err);
    }
  }

  public handleResendToFactoreCode(): void {
    this.authCode = "";
    this.twoFactorHttpService.resendTwoFactorCodeToEmail().subscribe({
      next: (response) => {
        this.isEmailCodeSend = true;
      },
      error: (error) => {
        console.error(error);
        this.isEmailCodeSend = false;

        if (error?.error?.message) {
          this.errors = [error.error.message];
        } else if (error?.error?.verify_token) {
          this.errors = [error.error.verify_token];
        } else if (error?.error?.otp) {
          this.errors = [error.error.otp];
        } else {
          this.errors = [this.translate.instant("TWO_FACTOR_MODAL.ERROR")];
        }
      }
    });
  }

  public handleAuthCodeValue(value: string): void {
    if (this.isEmailCodeSend) this.isEmailCodeSend = false;
    this.authCode = value;
  }

  public goToApp(): void {
    this.dialogRef.close();
  }

  public downloadStaticTokens(): void {
    if (DeviceRecognizerHelper.isAndroidWebview() || DeviceRecognizerHelper.isIosWebview()) {
      //we simulate open URL for mobile APP. In mobile APP we catch this url, block open new window and paste this url to method for downloading content
      window.open(this.tokensDownloadUrl, "_self");
    } else {
      const content = this.staticTokens.join("\n");
      const blob = new Blob([content], { type: "text/plain" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "taxando_2fa_tokens.txt";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }
  }
}
